<template lang="pug">
  .search-bar.width-100
    .container.row.middle.center
      search-bar-select.m-right-1(
        name="topicIds" v-if='firstLoading || topicIdsOptions.length'
        :disabled='firstLoading || loading'
        @change='editSelect'
        :options='topicIdsOptions'
        :placeholder='$t("feed.searchbar.topics")'
        :value='getSelectValue("topicIds")'
      )
      search-bar-select.m-right-1(
        name="months" v-if='firstLoading || monthsOptions.length'
        :disabled='firstLoading || loading'
        @change='editSelect'
        :options='monthsOptions'
        :placeholder='$t("feed.searchbar.ephemeride")'
        :value='getSelectValue("months")'
      )
      search-bar-select.m-right-1(
        name="skillIds" v-if='firstLoading || skillIdsOptions.length'
        :disabled='firstLoading || loading'
        @change='editSelect'
        :options='skillIdsOptions'
        :placeholder='$t("feed.searchbar.skills")'
        :value='getSelectValue("skillIds")'
      )
      search-bar-select.m-right-1(
        name="ODSIds" v-if='firstLoading || odsIdsOptions.length'
        :disabled='firstLoading || loading'
        @change='editSelect'
        :options='odsIdsOptions'
        :placeholder='$t("feed.searchbar.sdg")'
        :value='getSelectValue("ODSIds")'
      )
      search-bar-select.m-right-1(
        name="ageRangeIds" v-if='firstLoading || ageRangeIdsOptions.length'
        :disabled='firstLoading || loading'
        @change='editSelect'
        :options='ageRangeIdsOptions'
        :placeholder='$t("feed.searchbar.ageRange")'
        :value='getSelectValue("ageRangeIds")'
      )
      search-bar-select.m-right-1(
        name="subjectIds" v-if='firstLoading || subjectIdsOptions.length'
        :disabled='firstLoading || loading'
        @change='editSelect'
        :options='subjectIdsOptions'
        :placeholder='$t("feed.searchbar.subjects")'
        :value='getSelectValue("subjectIds")'
      )
      search-input(:disabled='firstLoading || loading')
      router-link.text-normal.bold.white.m-left-2(
        v-if='hasFiltersSelected'
        :to='{name: routes.feedInitial}'
      ) {{ $t("feed.searchbar.clear") }}
</template>

<script>
import SearchBarSelect from './SearchBarSelect'
import userRole from '../../mixins/userRole'
import { mapActions, mapState } from 'vuex'
import { i18n } from '../../main'
import moment from 'moment'
import { capitalize } from '@amcharts/amcharts4/.internal/core/utils/Utils'
import { deepCopy } from '../../utils/object'
import { routes } from '../../router'
import SearchInput from './SearchInput'
import { localesToMoment } from '../../constants/languages'

export default {
  mixins: [userRole],
  components: { SearchBarSelect, SearchInput },
  data() {
    return {
      routes,
      firstLoading: false,
      loading: false
    }
  },
  async created() {
    this.firstLoading = true
    await this.hydrateProfile()
    await this.hydrateAvailableFilters()
    this.firstLoading = false
  },
  watch: {
    async selectedFilters() {
      this.loading = true
      await this.hydrateAvailableFilters()
      this.loading = false
    },
    async locale() {
      this.loading = true
      await this.hydrateAvailableFilters()
      this.loading = false
    }
  },
  computed: {
    ...mapState({
      locale: state => state.behavior.locale,
      topics: state => state.newFeed.filters.topicIds,
      skills: state => state.newFeed.filters.skillIds,
      months: state => state.newFeed.filters.months,
      ods: state => state.newFeed.filters.ODSIds,
      ages: state => state.newFeed.filters.ageRangeIds,
      subjects: state => state.newFeed.filters.subjectIds,
      ephemerideInfo: state => state.newFeed.searchResults.ephemeride.data
    }),
    hasFiltersSelected() {
      return Object.keys(this.selectedFilters).length
    },
    selectedFilters() {
      return this.$route.query
    },
    topicIdsOptions() {
      return this.topics.map(topic => ({
        label: topic.name,
        value: topic.id
      }))
    },
    monthsOptions() {
      moment.locale(localesToMoment[this.locale])
      return moment
        .months()
        .map((month, index) => ({ label: capitalize(month), value: index + 1 }))
        .filter((month, index) =>
          this.$route.name === routes.feedInitial
            ? true
            : this.months.includes(index + 1)
        )
    },
    skillIdsOptions() {
      return this.skills.map(skill => ({
        value: skill.id,
        label: this.$t(`skills.${skill.translationId}`)
      }))
    },
    odsIdsOptions() {
      return this.ods.map(ods => ({
        value: ods.id,
        label: this.$t(`sdg.${ods.translationId}`)
      }))
    },
    ageRangeIdsOptions() {
      return this.ages
        .filter(age => this.userEducationalLevels.includes(age.educationalLevel))
        .map(age => ({
          value: age.id,
          label: `\
          <div class='age-option' style='--color: ${age.mainColor}'>\
            ${this.$t('resourceCard.agesTag', { min: age.minAge, max: age.maxAge })}\
          </div>`
        }))
    },
    subjectIdsOptions() {
      return this.subjects.map(sub => ({
        value: sub.id,
        label: i18n.t(`subjects.${sub.translationId}`)
      }))
    }
  },
  methods: {
    ...mapActions([
      'hydrateProfile',
      'hydrateAvailableFilters'
    ]),
    getSelectValue(name) {
      if (!this.selectedFilters[name]) return []
      return Array.isArray(this.selectedFilters[name])
        ? this.selectedFilters[name].map(val => Number(val))
        : [Number(this.selectedFilters[name])]
    },
    editSelect(name, value, removed) {
      const query = deepCopy(this.$route.query)
      if (removed) {
        Array.isArray(query[name])
          ? query[name] = query[name].filter(op => op !== value)
          : delete query[name]
        for (const key in query) {
          Array.isArray(query[key]) && query[key].length === 0 && delete query[key]
        }
        this.$router.push({
          name: Object.keys(this.selectedFilters).length ? routes.search : routes.feedInitial,
          params: this.$route.params,
          query
        })
      } else {
        if (!query[name]) {
          query[name] = value
        } else if (Array.isArray(query[name])) {
          query[name].push(value)
        } else {
          query[name] = [query[name], value]
        }
        this.$router.push({
          name: this.$route.name === routes.search ? this.$route.name : routes.search,
          params: this.$route.params,
          query
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';

.search-bar{
  height: 75px;
  background-color: $blue;
}
.container{
  margin: 0 auto;
  height: 100%;
  max-width: 1180px;
}
</style>
